import { ADD_PRODUCT_BASIC, type addBaseProductDataAction } from "~/domains/product/redux/basicProductActions";
import { isKITProduct, type ProductData } from "~/domains/product/types/ProductData";
import {
  type fetchSRPCampaignRecommendationsSuccess,
  SEARCH_SUCCESS,
  type searchSuccessAction,
  SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS,
} from "~/features/search/redux/actions";
import { combineReducers } from "redux";
import {
  CHECKOUT_LOAD_DATA,
  type checkoutLoadDataAction,
} from "~/features/cart/cart/redux/actions/checkoutPageActions";
import {
  HSA_SEARCH_SUCCESS,
  LOAD_INITIAL_DATA,
  type loadInitialDataAction,
  type searchSuccessAction as hsaSearchSuccessAction,
} from "~/features/selectionAssistant/housing/redux/actions";
import { CART_LOAD_SUCCESS, type cartLoadFinishAction } from "~/features/cart/cart/redux/actions";
import { produce } from "immer";

import { isNonNull } from "~/common/utils/reducers";
import {
  FINISH_FETCH_RELATED_PRODUCTS,
  type RelatedProductsActions,
} from "~/domains/product/redux/relatedProducts/actions";
import { relatedProductsReducer } from "~/domains/product/redux/relatedProducts/reducer";
import { topsellerProductsReducer } from "~/domains/product/redux/topsellerProducts/reducer";
import {
  FINISH_FETCH_TOPSELLER_PRODUCTS,
  type TopsellerProductsActions,
} from "~/domains/product/redux/topsellerProducts/actions";
import type { SrpProductData } from "~/utils/models";
import { fccRecommendedProductsReducer } from "~/domains/product/redux/fccRecommendationProducts/reducer";
import {
  type FCCRecommendedProductsActions,
  FINISH_FETCH_FCC_RECOMMENDED_PRODUCTS,
} from "~/domains/product/redux/fccRecommendationProducts/actions";

type BaseProductStore = { [code: string]: ProductData };

export type BaseProductReducerActions =
  | ReturnType<typeof searchSuccessAction>
  | ReturnType<typeof cartLoadFinishAction>
  | ReturnType<typeof checkoutLoadDataAction>
  | ReturnType<typeof addBaseProductDataAction>
  | ReturnType<typeof loadInitialDataAction>
  | ReturnType<typeof hsaSearchSuccessAction>
  | ReturnType<typeof fetchSRPCampaignRecommendationsSuccess>
  | RelatedProductsActions
  | TopsellerProductsActions
  | FCCRecommendedProductsActions;

/**
 * Products from wishlistApi.getWishlists are dispatched directly via
 * addBaseProductDataAction in the queryFn, so no matchFulfilled listener is needed.
 */
const baseProductsReducer = (state: BaseProductStore = {}, action: BaseProductReducerActions): BaseProductStore => {
  switch (action.type) {
    case SEARCH_SUCCESS: {
      const { data } = action.payload;
      return updateBasicProducts(state, data.products);
    }

    case CART_LOAD_SUCCESS: {
      return updateBasicProducts(state, action.payload.products);
    }

    case CHECKOUT_LOAD_DATA: {
      return updateBasicProducts(state, action.payload.data.products);
    }

    case LOAD_INITIAL_DATA:
    case HSA_SEARCH_SUCCESS: {
      return updateBasicProducts(state, action.payload.searchResults.products);
    }

    case ADD_PRODUCT_BASIC: {
      const { products } = action.payload;
      return updateBasicProducts(state, products);
    }

    case FINISH_FETCH_RELATED_PRODUCTS: {
      const { relatedProducts } = action.payload;
      return updateBasicProducts(state, relatedProducts as ProductData[]);
    }

    case FINISH_FETCH_TOPSELLER_PRODUCTS: {
      const { topsellerProducts } = action.payload;
      return updateBasicProducts(state, topsellerProducts as ProductData[]);
    }

    case SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS: {
      const products = action.payload as SrpProductData[];
      return updateBasicProducts(state, products);
    }

    case FINISH_FETCH_FCC_RECOMMENDED_PRODUCTS: {
      const { fccRecommendedProducts } = action.payload;
      return updateBasicProducts(state, fccRecommendedProducts as ProductData[]);
    }

    default:
      return state;
  }
};

const combinedProductsReducer = combineReducers({
  basic: baseProductsReducer,
  relatedProducts: relatedProductsReducer,
  topsellerProducts: topsellerProductsReducer,
  fccRecommendedProducts: fccRecommendedProductsReducer,
});

/**
 * Product responses are a legacy Redux endpoint cache. A Sold-To Account transition must
 * remove them before any new-account request can render.
 */
export const productsReducer = combinedProductsReducer;

interface CollectedProducts {
  cloned: ProductData;
  all: ProductData[];
}

/**
 * Single-pass function that simultaneously:
 * 1. Clones the product with safe (deduplicated) predecessors
 * 2. Collects all nested predecessor products into a flat array
 *
 * Replaces the old separate `cloneWithSafePredecessors` + `collectPredecessorProducts` functions
 * which both traversed the same predecessor tree independently.
 */
function cloneAndCollectProducts(product: ProductData, seenCodes: Set<string> = new Set()): CollectedProducts {
  const nextSeenCodes = new Set(seenCodes);
  nextSeenCodes.add(product.code);

  const collected: ProductData[] = [];

  const safePredecessors = (product.predecessorProducts ?? []).reduce<ProductData[]>((acc, pred) => {
    if (!pred?.code || nextSeenCodes.has(pred.code)) {
      return acc;
    }
    if (acc.some((item) => item.code === pred.code)) {
      return acc;
    }
    const nested = cloneAndCollectProducts(pred, nextSeenCodes);
    acc.push(nested.cloned);
    collected.push(nested.cloned, ...nested.all);
    return acc;
  }, []);

  const cloned: ProductData = {
    ...product,
    predecessorProducts: safePredecessors,
  };

  return { cloned, all: collected };
}

function updateBasicProducts(state: BaseProductStore, products: ProductData[]) {
  // Do not add products without a code
  const validProducts = products.filter((product) => product.code);

  // Single pass: clone predecessors AND collect all nested products
  const allProducts: ProductData[] = [];

  for (const product of validProducts) {
    const { cloned, all } = cloneAndCollectProducts(product);

    // Collect successor
    if (cloned.successorProduct) {
      allProducts.push(cloned.successorProduct);
    }
    // Collect alternatives
    if (cloned.alternativeProducts) {
      allProducts.push(...cloned.alternativeProducts.filter(isNonNull));
    }
    // Collected predecessors (already in `all` from single-pass)
    allProducts.push(...all);
    // Collect KIT parts
    if (isKITProduct(cloned)) {
      allProducts.push(...cloned.includedParts.map((i) => i.product).filter(isNonNull));
      allProducts.push(...cloned.optionalParts.map((i) => i.product).filter(isNonNull));
    }
    // The product itself goes last (highest priority)
    allProducts.push(cloned);
  }

  return produce(state, (draft) => {
    allProducts.forEach((product) => {
      const oldProduct = draft[product.code];
      if (oldProduct?.isFullProduct) {
        return;
      }
      if (oldProduct) {
        draft[product.code] = merge(oldProduct, product);
      } else {
        draft[product.code] = product;
      }
    });
  });
}

function merge<T extends object>(base: T, other: T): T {
  const result = { ...base };
  // Add all properties from other as long as they are not equal undefined
  getKeys(other).forEach((key) => {
    const thisValue = base[key];
    const otherValue = other[key];

    if (!isOverridableValue(otherValue)) {
      return;
    }

    if (isArrayValue(thisValue, otherValue)) {
      result[key] = overrideArray(thisValue, otherValue);
    } else if (isObject(thisValue) && isObject(otherValue)) {
      if (Object.keys(thisValue).length > 0 && Object.keys(otherValue).length > 0) {
        result[key] = merge(thisValue, otherValue);
      } else if (Object.keys(thisValue).length === 0) {
        result[key] = otherValue;
      }
    } else {
      result[key] = otherValue;
    }
  });
  return result;
}

/**
 * The property is considered an array value if either this or other has an array in that property
 * @param thisValue
 * @param otherValue
 */
function isArrayValue(thisValue: unknown, otherValue: unknown) {
  return Array.isArray(thisValue) || Array.isArray(otherValue);
}

/**
 * If other is an array with items, return other.
 * If this is an array with items, return this.
 * Otherwise just return the one that is an array
 * @param thisValue
 * @param otherValue
 */
function overrideArray<V>(thisValue: V, otherValue: V): V {
  if (Array.isArray(otherValue) && otherValue.length > 0) {
    return otherValue;
  }
  if (Array.isArray(thisValue) && thisValue.length > 0) {
    return thisValue;
  }
  return Array.isArray(otherValue) ? otherValue : thisValue;
}

function isOverridableValue(value: unknown) {
  if (value === undefined) {
    return false;
  }

  return true;
}

function getKeys<T extends object>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

function isObject(value: unknown): value is object {
  return value !== null && typeof value === "object";
}
